import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/api";
import { toast } from "react-toastify";

export const PendingOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusOrderId = searchParams.get("orderId") || null;

  // who am I?
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("sp_user") || "{}"); } catch { return {}; }
  }, []);
  const role = me?.role;
  const isBuyer  = role === "user" || role === "buyer";
  const isVendor = role === "vendor" || me?.isVendor === true;

  // state
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // load one pending order (specific id via ?orderId=... or first pending)
  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = isVendor ? await api.orders.vendorMine() : await api.orders.mine();
      const list = Array.isArray(res?.orders) ? res.orders : Array.isArray(res) ? res : [];
      const pendings = list.filter(o => o.status === "pending");

      if (focusOrderId) {
        setOrder(pendings.find(o => (o._id || o.orderId) === focusOrderId) || null);
      } else {
        setOrder(pendings[0] || null);
      }
    } catch (e) {
      setErr(e?.data?.message || e?.data?.error || e?.message || "Failed to load pending order");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor, isBuyer, focusOrderId]);

  // helpers
  const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString()}`;
  const orderId = order?._id || order?.orderId || "";
  const createdAt = order ? new Date(order.createdAt || order.timestamp || Date.now()).toLocaleString() : "";

  // phones
  const buyerPhone = order?.buyerPhone || order?.customerPhone || "-";
  const vendorPhone = (() => {
    const items = Array.isArray(order?.items) ? order.items : [];
    const prod = items[0]?.productId && typeof items[0].productId === "object" ? items[0].productId : null;
    // be tolerant to different field names from product creation
    return prod?.sellerPhone || prod?.vendorPhone || prod?.phone || "-";
  })();

  // actions
  const assignRider = async () => {
    if (!orderId) return;
    try {
      const check = await api.orders.transitionCheck(orderId, "processing");
      if (!check?.allowed) return toast.warn(check?.error || "You can’t move this order to processing.");
      navigate(`/orders/available-riders?orderId=${orderId}`);
    } catch (e) {
      toast.warn(e?.data?.message || e?.data?.error || "Failed to check transition");
    }
  };

  const cancelOrder = async () => {
    if (!orderId) return;
    if (!confirm("Cancel this order?")) return;
    try {
      const check = await api.orders.transitionCheck(orderId, "cancelled");
      if (!check?.allowed) return toast.warn(check?.error || "You can’t cancel this order.");
      await api.orders.transition(orderId, "cancelled");
      toast.warn("Order cancelled.");
      navigate("/orders");
    } catch (e) {
      toast.warn(e?.data?.message || e?.data?.error || "Failed to cancel order");
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-40">
        {/* Topbar */}
        <div className="w-full flex items-center mt-4">
          <button onClick={() => navigate(-1)} className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">Pending Order</h1>
          <div className="w-9" />
        </div>

        {/* order number */}
        <div className="px-4 mt-3 text-[14px]">
          {order ? `Order #${order.orderId || orderId}` : ""}
        </div>

        {/* Customer summary — DYNAMIC */}
        <div className="px-4 mt-2">
          <div className="rounded-[12px] bg-[#eadfb7] p-3">
            <div className="flex items-start justify-between">
              <div>
                {/* Left block content switches by role */}
                {order ? (
                  <>
                    {/* Name + email */}
                    <p className="font-bold">
                      {isVendor
                        ? (order.buyerName || order.customerName || "")
                        : (order.vendorName || order.sellerName || "")}
                    </p>
                    <p className="text-[13px] mt-1">
                      {isVendor
                        ? (order.buyerEmail || order.customerEmail || "")
                        : (order.vendorEmail || order.sellerEmail || "")}
                    </p>

                    {/* When */}
                    <p className="text-[13px]">{createdAt}</p>

                    {/* Phone by role */}
                    <p className="text-[13px]">
                      {isVendor ? (order.buyerPhone || "-") : (order.vendorPhone || "-")}
                    </p>

                    {/* Status pill */}
                    <span className="inline-block mt-2 text-[12px] px-2 py-1 rounded-full bg-[#f7e8a6] text-[#7d5d00]">
                      Pending
                    </span>
                  </>
                ) : (
                  <p className="text-[13px] opacity-70">
                    {loading ? "Loading…" : err || "No pending order found."}
                  </p>
                )}
              </div>

              {/* Total */}
              <p className="font-bold">
                {order ? fmtNaira(order.totalAmount) : ""}
              </p>
            </div>
          </div>
        </div>

        {/* Address — DYNAMIC */}
        <div className="px-4 mt-5">
          <p className="font-extrabold">Delivery Address</p>
          <div className="mt-2 rounded-[12px] bg-[#eadfb7] p-3">
            {order ? (
              <>
                <p>{order?.shippingAddress?.street || "-"}</p>
                <p>
                  {[order?.shippingAddress?.city, order?.shippingAddress?.state].filter(Boolean).join(", ") || "-"}
                </p>
                <p>{order?.shippingAddress?.country || "-"}</p>
              </>
            ) : (
              <p className="opacity-70">{loading ? "Loading…" : "-"}</p>
            )}
          </div>
        </div>

        {/* Items — DYNAMIC */}
        <div className="px-4 mt-5">
          <p className="font-extrabold">Items</p>
          <div className="mt-3 space-y-3">
            {order && Array.isArray(order.items) && order.items.length > 0 ? (
              order.items.map((it, i) => {
                // populated product
                const prod = it.productId && typeof it.productId === "object" ? it.productId : null;
                const img = prod?.images?.[0] || "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=200&q=80&auto=format&fit=crop";
                const name = it?.name || prod?.name || "Item";
                const unit = fmtNaira(it?.pricePerUnit);
                return (
                  <div key={i} className="rounded-[10px] bg-[#eadfb7] px-2 py-2 flex items-center">
                    <img alt="" src={img} className="w-12 h-12 rounded-[8px] object-cover" />
                    <div className="ml-3">
                      <p className="font-bold text-[14px]">{name}</p>
                      <p className="text-[12px]">{unit}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-[13px] opacity-70">
                {loading ? "Loading…" : "No items."}
              </div>
            )}
          </div>
        </div>

        {/* Actions — ROLE-SPECIFIC */}
        <div className="px-4 mt-6 flex flex-col items-center">
          {isVendor ? (
            <button
              onClick={assignRider}
              disabled={!order}
              className="w-[290px] h-[56px] rounded-[28px] bg-[#f7c945] font-bold text-[#5d4037] hover:opacity-90 transition-opacity disabled:opacity-70"
            >
              Assign Rider
            </button>
          ) : (
            <button
              onClick={cancelOrder}
              disabled={!order}
              className="w-[290px] h-[56px] rounded-[28px] bg-[#f7c945] font-bold text-[#5d4037] hover:opacity-90 transition-opacity disabled:opacity-70"
            >
              Cancel Order
            </button>
          )}
          <button onClick={() => navigate(-1)} className="mt-4 text-[#c34040] underline">
            Back
          </button>
        </div>

        {/* spacer */}
        <div className="h-6" />

        {/* Safe-area bar */}
        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
